import React, { useEffect, useMemo } from "react";
import { Grid } from '@mui/material'
import { Button, Input, Checkbox } from '@fullscreendigital/carrefour-dex-fe'
import { Modal } from 'components/core-ui/Modal'
import { useFormik } from 'formik'
import { getInitialValues, getValidationSchema } from './helpers'
import { useNotification } from 'hooks'
import { formatErrorMessage } from 'utils/formatting'
import { makeCorePostRequest, makeCorePutRequest } from 'utils/api'
import { useMutation } from 'react-query'

interface NewAdminDepositProps {
  isModalOpen: boolean
  closeModal: Function
  setShouldRefreshData: Function
  isEdit?: boolean
  editItem?: PackingType
}

const PackagingTypesForm: React.FC<NewAdminDepositProps> = props => {
  const { isModalOpen, closeModal, isEdit, editItem, setShouldRefreshData } = props
  const { showNotification } = useNotification()

  const initialValues = useMemo(() => getInitialValues(editItem), [editItem])
  const validationSchema = useMemo(() => getValidationSchema(), [])

  const createUpdatePackage = async (values: any) => {
    isEdit
      ? await makeCorePutRequest(`/shop-reception/ds-support-types/${editItem.id}`, values)
      : await makeCorePostRequest(`/shop-reception/ds-support-types`, values)
  }

  const { isLoading, mutate } = useMutation(createUpdatePackage, {
    onSuccess: () => {
      formik.resetForm()
      setShouldRefreshData(true)
      closeModal()
      showNotification({
        message: `Datele au fost ${isEdit ? 'actualizate' : 'create'} cu succes.`,
        type: 'success'
      })
    },
    onError: res => {
      showNotification({
        message: `A aparut o eroare la ${
          isEdit ? 'actualizarea' : 'crearea'
        } datelor. ${formatErrorMessage(res)}`,
        type: 'error'
      })
    }
  })

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values: any) => {
      const payload = {
        ...values,
        childrenIds: values.childrenIds.map(child => child.value)
      }
      mutate(payload)
    }
  })

  useEffect(() => {
    if (isModalOpen) {
      formik.resetForm({ values: initialValues })
    }
  }, [isModalOpen, initialValues])

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={closeModal}
      modalTitle={isEdit ? `Editează tip ambalaj ${editItem?.code}` : 'Adaugă tip ambalaj'}
      modalWidth="50%"
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Input
            variant="outlined"
            label="Cod ambalaj"
            type="text"
            fullWidth
            onChange={(e: any) => formik.setFieldValue('name', e.target.value)}
            value={formik.values.name}
            error={!!formik.errors.name}
            helperText={formik.errors.name as string}
          />
        </Grid>
        <Grid item xs={12}>
          <Input
            variant="outlined"
            label="Denumire"
            type="text"
            fullWidth
            onChange={(e: any) => formik.setFieldValue('code', e.target.value)}
            value={formik.values.code}
            error={!!formik.errors.code}
            helperText={formik.errors.code as string}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <Checkbox
            checked={formik.values.active}
            defaultChecked={formik.values.active}
            onChange={() => formik.setFieldValue('active', !formik.values.active)}
            label="Activ"
          />
        </Grid>
        <Grid item xs={12}>
          <Checkbox
            checked={formik.values.returnable}
            defaultChecked={formik.values.returnable}
            onChange={() => formik.setFieldValue('returnable', !formik.values.returnable)}
            label="Returnabil"
          />
        </Grid>

        <Grid item xs={12} container justifyContent="space-between">
          <Grid>
            <Button size="large" variant="outlined" label="Anuleaza" onClick={() => closeModal()} />
          </Grid>

          <Grid>
            <Button
              size="large"
              variant="contained"
              label="Salveaza"
              onClick={() => formik.handleSubmit()}
              disabled={isLoading}
            />
          </Grid>
        </Grid>
      </Grid>
    </Modal>
  )
}

export default PackagingTypesForm
